import * as React from "react";
import 'bootstrap/dist/css/bootstrap.min.css';
import { useContext, useEffect, useState } from "react";
import { HttpContext } from '../../api-services/HttpService';
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from 'redux';
import { actionCreators } from '../../redux/index';
import { Skeleton } from '@mui/material';

// Type definitions
interface FooterConfig {
    contactEmail: string;
    releaseVersion: string;
}

interface RootState {
    footerConfig?: FooterConfig;
    [key: string]: any;
}

const PRIVACY_POLICY_URL = 'https://www.argusinformation.com/privacy-notice/';

type Props = {
    cssClassName?: string;
}

const Footer = ({cssClassName}: Props) => {
    
    // Context and state
    const { GET } = useContext(HttpContext);
    const footerConfig = useSelector((state: RootState) => state.footerConfig);
    const dispatch = useDispatch();
    const { setFooterConfig } = bindActionCreators(actionCreators, dispatch);
    const [isLoading, setIsLoading] = useState(false);

    const fetchFooterConfig = async () => {
        try {
            // Redux State: Only stores actual data from the API response, never default values
            // API Call: Only stores data in Redux if the API actually returns contactEmail or latestReleaseVersion
            // Display: Shows skeletons while loading, real values when available, hides elements if no data
            
            // Page Load → Show Skeletons → API Call (background) → Success? → Update Display
            //      ↓           ↓              ↓                      ↓           ↓
            //    Empty {}  → Skeletons   → Still Skeletons     → Real Data → Real Values or Hidden
            
            // Check if Redux state has meaningful data for both fields
            const hasValidEmail = footerConfig?.contactEmail && footerConfig.contactEmail.trim();
            const hasValidVersion = footerConfig?.releaseVersion && footerConfig.releaseVersion.trim();
            
            if (hasValidEmail && hasValidVersion) {
                // Already have complete data in Redux, no need to fetch
                return;
            }

            // Start loading
            setIsLoading(true);

            // Make API call to get fresh data
            const response = await GET('/data/footerContent');
            
            if (response?.data && (response.data.contactEmail?.trim() || response.data.latestReleaseVersion?.trim())) {
                const newFooterConfig: FooterConfig = {
                    contactEmail: response.data.contactEmail?.trim() || '',
                    releaseVersion: response.data.latestReleaseVersion?.trim() || ''
                };
                
                // Only store actual data from API, not defaults
                setFooterConfig(newFooterConfig);
            }
        } catch (error) {
            console.error('Failed to fetch footer config:', error);
            // Elements will be hidden on error
        } finally {
            // Stop loading
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchFooterConfig();
    }, []);

    return (
    <>
        <div className={`footer-wrapper gx-0 dashboard-footer ${cssClassName}`}>
            <div className="fs-12 footer ms-4 me-0 d-flex align-items-center justify-content-between ${cssClassName}? '': p-2 mt-4'">
                <div className="mt-1 d-flex align-items-center gap-1">
                    <div className="footer-text">
                        © Copyright 2026 TransUnion LLC. All Rights Reserved.
                    </div>

                    {(isLoading ||
                        (footerConfig?.releaseVersion &&
                            footerConfig.releaseVersion.trim())) && (
                        <>
                            <div
                                className="vr"
                                style={{
                                    width: "1.2px",
                                    alignSelf: "auto",
                                    color: "#3D3D3D",
                                    opacity: "0.5",
                                }}
                            ></div>

                            {isLoading ? (
                                <div className="footer-text">
                                    Release Version:{" "}
                                    <Skeleton
                                        variant="text"
                                        width={60}
                                        height={16}
                                        sx={{ display: "inline-block" }}
                                    />
                                </div>
                            ) : (
                                <div className="footer-text">
                                    Release Version: {footerConfig?.releaseVersion}
                                </div>
                            )}
                        </>
                    )}
                </div>
                <div className="mt-1 policy">
                    <a
                        href={PRIVACY_POLICY_URL}        
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Privacy Policy
                    </a>
                </div>
            </div>
        </div>
    </>
);
}

export default Footer;